import React, { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { Flex, H4, P, Button, Loading, Icon, Pagination } from 'design-system-web';
import type { ApolloError } from '@apollo/client';
import Toggle from 'components/form/Toggle';

import { FONT_AWESOME_SOLID } from 'lane-shared/helpers/constants/icons';

import type { CustomFieldDetailModel } from './types';
import { getVisitorCustomFieldTypeDisplayLabel } from '../../../../feature/components/CustomFieldsSection/fieldTypeDisplayLabel';

import styles from './CustomFieldsListBody.scss';

const COLUMN_COUNT = 5;
const ADMIN_LIST_PAGE_SIZE = 10;

function formatCustomFieldListDatetime(value: string | null | undefined): string {
  if (!value) {
    return '--';
  }

  const timestamp = Date.parse(value);

  if (Number.isNaN(timestamp)) {
    return value;
  }

  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}


function getDatetimeSortValue(value: string | null | undefined): number {
  const parsed = value ? Date.parse(value) : Number.NEGATIVE_INFINITY;

  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

type CustomFieldsListBodyProps = {
  isListLoading: boolean;
  configsCalled: boolean;
  configsError: ApolloError | undefined;
  tableRows: readonly CustomFieldDetailModel[];
  searchValue: string;
  onSelectRow?: (row: CustomFieldDetailModel) => void;
  onCreateClick?: () => void;
  onToggleStatus?: (row: CustomFieldDetailModel) => Promise<boolean>;
  isStatusUpdateLoading?: boolean;
  t: TFunction;
};

type SortKey = 'fieldName' | 'fieldType' | 'createdAtDatetime' | 'updatedAtDatetime' | 'status';
type SortDirection = 'asc' | 'desc';

type SortableColumnHeaderProps = {
  columnKey: SortKey;
  label: string;
  sortKey: SortKey | null;
  sortDirection: SortDirection;
  onSort: () => void;
  className?: string;
};

function SortableColumnHeader({
  columnKey,
  label,
  sortKey,
  sortDirection,
  onSort,
  className,
}: SortableColumnHeaderProps): React.ReactElement {
  const isActive = sortKey === columnKey;
  let ariaSort: 'none' | 'ascending' | 'descending' = 'none';

  if (isActive) {
    ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  return (
    <th scope="col" aria-sort={ariaSort} className={className}>
      <Flex align="center" gap={2}>
        {label}
        <button
          type="button"
          className={styles.sortIconButton}
          onClick={onSort}
          data-test={`sort-btn-${columnKey}`}
        >
          {!isActive && (
            <Icon
              set="FontAwesome"
              name="sort"
              type={FONT_AWESOME_SOLID}
              className={styles.disabledSortingIcon}
            />
          )}
          {isActive && (
            <Icon
              set="FontAwesome"
              name={sortDirection === 'asc' ? 'sort-up' : 'sort-down'}
              type={FONT_AWESOME_SOLID}
              className={styles.selectedSortingIcon}
            />
          )}
        </button>
      </Flex>
    </th>
  );
}

export function CustomFieldsListBody({
  isListLoading,
  configsCalled,
  configsError,
  tableRows,
  searchValue,
  onSelectRow,
  onCreateClick,
  onToggleStatus,
  isStatusUpdateLoading = false,
  t,
}: CustomFieldsListBodyProps): React.ReactElement {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue]);

  const handleSort = (column: SortKey): void => {
    if (column === sortKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));

      return;
    }

    setSortKey(column);
    setSortDirection('asc');
    setCurrentPage(0);
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) {
      return tableRows;
    }

    const direction = sortDirection === 'asc' ? 1 : -1;

    const getStringValue = (row: CustomFieldDetailModel): string => {
      switch (sortKey) {
        case 'fieldName':
          return row.fieldName.toLowerCase();
        case 'fieldType':
          return getVisitorCustomFieldTypeDisplayLabel(row.fieldType, t).toLowerCase();
        case 'status':
          return (
            row.isEnabled
              ? t('web.admin.visitorManagement.customField.status.enabled')
              : t('web.admin.visitorManagement.customField.status.disabled')
          ).toLowerCase();
        default:
          return '';
      }
    };

    return [...tableRows].sort((a, b) => {
      if (sortKey === 'createdAtDatetime' || sortKey === 'updatedAtDatetime') {
        const left = getDatetimeSortValue(a[sortKey]);
        const right = getDatetimeSortValue(b[sortKey]);

        return (left - right) * direction;
      }

      return getStringValue(a).localeCompare(getStringValue(b)) * direction;
    });
  }, [sortDirection, sortKey, t, tableRows]);

  const paginatedRows = sortedRows.slice(
    currentPage * ADMIN_LIST_PAGE_SIZE,
    (currentPage + 1) * ADMIN_LIST_PAGE_SIZE
  );

  if (!configsCalled || isListLoading || isStatusUpdateLoading) {
    return <Loading />;
  }

  if (configsError) {
    return (
      <div className={styles.listLoadError} role="alert">
        <P>
          {configsError.message ?? t('web.admin.visitorManagement.customField.error.loading')}
        </P>
      </div>
    );
  }

  return (
    <div data-testid="custom-fields-list" className={styles.tableWrap}>
      <table className={styles.fieldsTable} role="table">
        <thead>
          <tr>
            <SortableColumnHeader
              columnKey="status"
              label={t('web.admin.visitorManagement.customField.status.enabled')}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={() => handleSort('status')}
            />
            <SortableColumnHeader
              columnKey="fieldName"
              label={t('web.admin.visitorManagement.customField.table.fieldName')}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={() => handleSort('fieldName')}
            />
            <SortableColumnHeader
              columnKey="fieldType"
              label={t('web.admin.visitorManagement.customField.table.fieldType')}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={() => handleSort('fieldType')}
              className={styles.fieldTypeHeader}
            />
            <SortableColumnHeader
              columnKey="createdAtDatetime"
              label={t('web.admin.visitorManagement.customField.table.createdAt')}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={() => handleSort('createdAtDatetime')}
            />
            <SortableColumnHeader
              columnKey="updatedAtDatetime"
              label={t('web.admin.visitorManagement.customField.table.updatedAt')}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={() => handleSort('updatedAtDatetime')}
            />
          </tr>
        </thead>
        <tbody>
          {sortedRows.length > 0 ? (
            paginatedRows.map(row => (
              <tr
                key={row.id}
                data-testid={`custom-field-row-${row.id}`}
                className={onSelectRow ? styles.clickableRow : undefined}
                role={onSelectRow ? 'button' : undefined}
                tabIndex={onSelectRow ? 0 : undefined}
                aria-label={
                  onSelectRow
                    ? t('web.admin.visitorManagement.customField.table.openRowDetails', {
                        fieldName: row.fieldName,
                      })
                    : undefined
                }
                onClick={() => {
                  if (onSelectRow) {
                    onSelectRow(row);
                  }
                }}
                onKeyDown={(event) => {
                  if (onSelectRow && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onSelectRow(row);
                  }
                }}
              >
                <td
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <div className={styles.toggleCell}>
                    {onToggleStatus ? (
                      <Toggle
                        value={row.isEnabled}
                        onChange={() => { onToggleStatus(row); }}
                        disabled={isStatusUpdateLoading}
                        testId={`custom-field-toggle-${row.id}`}
                      />
                    ) : (
                      <span data-test={`custom-field-status-${row.id}`}>
                        {row.isEnabled
                          ? t('web.admin.visitorManagement.customField.status.enabled')
                          : t('web.admin.visitorManagement.customField.status.disabled')}
                      </span>
                    )}
                  </div>
                </td>
                <td className={`${styles.sectionNameCell} ${styles.fieldNameCell}`}>{row.fieldName}</td>
                <td>{getVisitorCustomFieldTypeDisplayLabel(row.fieldType, t)}</td>
                <td>{formatCustomFieldListDatetime(row.createdAtDatetime)}</td>
                <td>{formatCustomFieldListDatetime(row.updatedAtDatetime)}</td>
              </tr>
            ))
          ) : (
            <tr data-testid="custom-fields-list-empty">
              <td colSpan={COLUMN_COUNT} className={styles.emptyStateCell}>
                <div className={styles.emptyStateInner}>
                  <H4 className={styles.emptyStateTitle}>
                    {t('web.admin.visitorManagement.customField.emptyState.title')}
                  </H4>
                  <P className={styles.emptyStateDescription}>
                    {t('web.admin.visitorManagement.customField.emptyState.description')}
                  </P>
                  <Button variant="primary" onClick={onCreateClick}>
                    {t('web.admin.visitorManagement.customField.createButton')}
                  </Button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {sortedRows.length > ADMIN_LIST_PAGE_SIZE && (
        <Pagination
          page={currentPage}
          perPage={ADMIN_LIST_PAGE_SIZE}
          total={sortedRows.length}
          onPage={setCurrentPage}
          className={styles.pagination}
          testId="custom-fields-pagination"
        />
      )}
    </div>
  );
}
